"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, GripVertical } from "lucide-react";
import { useRef, useState } from "react";
import { cn } from "@/lib/utils";

/** Moves one item of a list to a new position. */
export function moveItem<T>(list: T[], from: number, to: number): T[] {
  const next = list.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}
type Controls = {
  /** Drag handle; also moves the item with arrow keys. */
  handle: React.ReactNode;
  /** "Move up/down" (list) or "Move left/right" (grid) buttons. */
  moveButtons: React.ReactNode;
  dragging: boolean;
};

/** A list (or grid) that can be reordered by dragging with a mouse or finger,
 *  with arrow keys on the handle, or with move buttons (FR-10, FR-12). */
export function SortableList(t0) {
  const $ = _c(16);
  const {
    items,
    itemKey,
    itemLabel,
    onReorder,
    label,
    layout: t1,
    className,
    itemClassName,
    renderItem
  } = t0;
  const layout = t1 === undefined ? "list" : t1;
  const refs = useRef([]);
  const [dragging, setDragging] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const grid = layout === "grid";
  let t2;
  if ($[0] !== itemLabel || $[1] !== items || $[2] !== onReorder) {
    t2 = function move(from, to) {
      if (to < 0 || to >= items.length || to === from) {
        return;
      }
      onReorder(from, to);
      setAnnouncement(`${itemLabel(items[from], from)} moved to position ${to + 1} of ${items.length}.`);
    };
    $[0] = itemLabel;
    $[1] = items;
    $[2] = onReorder;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const move = t2;
  const indexAt = function indexAt(x, y) {
    let best = -1;
    let bestDistance = Infinity;
    refs.current.forEach((el, i) => {
      if (!el) {
        return;
      }
      const r = el.getBoundingClientRect();
      const d = Math.hypot(x - (r.left + r.width / 2), y - (r.top + r.height / 2));
      if (d < bestDistance) {
        bestDistance = d;
        best = i;
      }
    });
    return best;
  };
  const back = grid ? "ArrowLeft" : "ArrowUp";
  const forward = grid ? "ArrowRight" : "ArrowDown";
  const t3 = grid ? "grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3" : "flex flex-col gap-2";
  let t4;
  if ($[4] !== className || $[5] !== t3) {
    t4 = cn(t3, className);
    $[4] = className;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = items.map((item, i_0) => {
    const name = itemLabel(item, i_0);
    const handle = <button type="button" aria-label={`Reorder ${name}. Drag, or use the ${grid ? "left and right" : "up and down"} arrow keys.`} className={cn("flex size-11 shrink-0 items-center justify-center rounded-lg text-ink hover:bg-sand disabled:opacity-30 disabled:hover:bg-transparent", "cursor-grab touch-none active:cursor-grabbing")} onPointerDown={e => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragging(i_0);
    }} onPointerMove={e_0 => {
      if (dragging === null) {
        return;
      }
      if (e_0.clientY < 72) {
        window.scrollBy(0, -16);
      } else {
        if (e_0.clientY > window.innerHeight - 160) {
          window.scrollBy(0, 16);
        }
      }
      const to_0 = indexAt(e_0.clientX, e_0.clientY);
      if (to_0 !== -1 && to_0 !== dragging) {
        onReorder(dragging, to_0);
        setDragging(to_0);
      }
    }} onPointerUp={() => {
      if (dragging !== null && dragging !== i_0) {
        setAnnouncement(`${name} moved to position ${dragging + 1}.`);
      }
      setDragging(null);
    }} onPointerCancel={() => setDragging(null)} onKeyDown={e_1 => {
      if (e_1.key === back || e_1.key === forward) {
        e_1.preventDefault();
        move(i_0, e_1.key === back ? i_0 - 1 : i_0 + 1);
      }
    }}><GripVertical className="size-5" aria-hidden="true" /></button>;
    const BackIcon = grid ? ArrowLeft : ArrowUp;
    const ForwardIcon = grid ? ArrowRight : ArrowDown;
    const moveButtons = <><button type="button" className="flex size-11 shrink-0 items-center justify-center rounded-lg text-ink hover:bg-sand disabled:opacity-30 disabled:hover:bg-transparent" disabled={i_0 === 0} onClick={() => move(i_0, i_0 - 1)} aria-label={`Move ${name} ${grid ? "left" : "up"}`}><BackIcon className="size-5" aria-hidden="true" /></button><button type="button" className="flex size-11 shrink-0 items-center justify-center rounded-lg text-ink hover:bg-sand disabled:opacity-30 disabled:hover:bg-transparent" disabled={i_0 === items.length - 1} onClick={() => move(i_0, i_0 + 1)} aria-label={`Move ${name} ${grid ? "right" : "down"}`}><ForwardIcon className="size-5" aria-hidden="true" /></button></>;
    return <li key={itemKey(item, i_0)} ref={el_0 => {
      refs.current[i_0] = el_0;
    }} className={cn(itemClassName, dragging === i_0 && "z-10 opacity-80 shadow-lg ring-2 ring-brand")}>{renderItem(item, i_0, {
        handle,
        moveButtons,
        dragging: dragging === i_0
      })}</li>;
  });
  let t6;
  if ($[7] !== label || $[8] !== t4 || $[9] !== t5) {
    t6 = <ul aria-label={label} className={t4}>{t5}</ul>;
    $[7] = label;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== announcement) {
    t7 = <p aria-live="polite" className="sr-only">{announcement}</p>;
    $[11] = announcement;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <>{t6}{t7}</>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}